/* In-memory InsightClient for mock mode (no gateway configured). Seeds a
 * published insight on a couple of catalog agents so the agent page demos the
 * found → applied flow, plus one advisory insight for the acknowledge flow, and
 * leaves the rest empty for the empty-state card. Deterministic (fixed
 * DASHBOARD_NOW, no randomness); errors mirror the contract `{ error }`
 * envelope and the BFF's transition rules. */

import type { InsightClient } from "./InsightClient";
import type { FeedbackVerdict, Insight } from "./types";
import type { ApiError } from "../analytics";
import { DASHBOARD_NOW } from "../analytics";

function notFound(insightId: string): { error: ApiError } {
  return {
    error: {
      code: "not_found",
      message: `insight_id '${insightId}' not found`,
    },
  };
}

function conflict(state: string, verb: string): { error: ApiError } {
  return {
    error: {
      code: "bad_request",
      message: `insight in state '${state}' cannot be ${verb}`,
    },
  };
}

const NOW = DASHBOARD_NOW.toISOString();
const MOCK_WORKSPACE_ID = "00000000-0000-4000-8000-00000000d0c5";

/* Fixed ids/copy (the found-card copy mirrors the Figma mock) so mock mode is
 * reproducible across reloads and tests. */
const SEED_INSIGHTS: readonly Insight[] = [
  {
    id: "11111111-1111-4111-8111-111111111111",
    workspace_id: MOCK_WORKSPACE_ID,
    agent_id: "cc-platform",
    state: "published",
    kind: "config_change",
    source: "manual",
    author: "mock@ai21.com",
    title: "Duplicate tool results are inflating this agent's context",
    narrative:
      "Strip repeated results, logging noise, and secrets before they reach " +
      "the model. Nothing changes in your agent's behavior — only in what " +
      "you pay for.",
    projected_savings: "≈22%",
    optimization_id: "tool_result_compression",
    config_suggestion: { local_compression: { enabled: true } },
    feedback_verdict: null,
    feedback_comment: null,
    created_at: NOW,
    updated_at: NOW,
  },
  {
    id: "22222222-2222-4222-8222-222222222222",
    workspace_id: MOCK_WORKSPACE_ID,
    agent_id: "cc-studio",
    state: "published",
    kind: "config_change",
    source: "manual",
    author: "mock@ai21.com",
    title: "Oversized tool results dominate token spend",
    narrative:
      "Cap runaway tool results at a safe length before they hit the " +
      "context window. Long outputs keep their head and tail.",
    projected_savings: "~18% fewer tokens / month",
    optimization_id: "tool_result_truncation",
    config_suggestion: { tool_result_truncation: { enabled: true } },
    feedback_verdict: null,
    feedback_comment: null,
    created_at: NOW,
    updated_at: NOW,
  },
  /* Advisory (OPSS-283): no config_suggestion, so nothing to apply — the card's
   * 👍/👎 are its only controls. Sits on cc-studio beside that agent's
   * config_change insight, so mock mode renders both card kinds in one rail. */
  {
    id: "33333333-3333-4333-8333-333333333333",
    workspace_id: MOCK_WORKSPACE_ID,
    agent_id: "cc-studio",
    state: "published",
    kind: "advisory",
    source: "manual",
    author: "mock@ai21.com",
    title: "Split the reviewer loop out of this agent",
    narrative:
      "Two thirds of this agent's turns re-read the same review checklist. " +
      "Run the review as its own agent so the checklist is cached once.",
    projected_savings: "≈15%",
    optimization_id: null,
    config_suggestion: null,
    feedback_verdict: null,
    feedback_comment: null,
    created_at: NOW,
    updated_at: NOW,
  },
];

export interface MockInsightClientOptions {
  delayMs?: number;
}

export class MockInsightClient implements InsightClient {
  private readonly delayMs: number;
  private readonly insights: Map<string, Insight>;

  constructor(opts: MockInsightClientOptions = {}) {
    this.delayMs = opts.delayMs ?? 180;
    this.insights = new Map(SEED_INSIGHTS.map((i) => [i.id, { ...i }]));
  }

  private async delay(): Promise<void> {
    if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs));
  }

  async list(agentId: string): Promise<Insight[]> {
    await this.delay();
    // Matches the BE: published only, newest first, unknown agent → [].
    return [...this.insights.values()]
      .filter((i) => i.agent_id === agentId && i.state === "published")
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map((i) => ({ ...i }));
  }

  async listAll(): Promise<Insight[]> {
    await this.delay();
    // Matches the BE's /all: every state, newest first.
    return [...this.insights.values()]
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map((i) => ({ ...i }));
  }

  async apply(insightId: string): Promise<Insight> {
    return this.transition(insightId, "applied", "applied");
  }

  async dismiss(insightId: string): Promise<Insight> {
    return this.transition(insightId, "dismissed", "dismissed");
  }

  async acknowledge(insightId: string): Promise<Insight> {
    return this.transition(insightId, "acknowledged", "acknowledged");
  }

  async submitFeedback(
    insightId: string,
    verdict: FeedbackVerdict,
    comment?: string,
  ): Promise<Insight> {
    await this.delay();
    const row = this.insights.get(insightId);
    if (!row) throw notFound(insightId);

    // Matches the BE: allowed in every customer-visible state, last write wins.
    if (row.state === "draft") throw conflict(row.state, "given feedback");

    const next: Insight = {
      ...row,
      feedback_verdict: verdict,
      feedback_comment: comment ?? null,
    };
    this.insights.set(insightId, next);
    return { ...next };
  }

  /** Shared published → target transition; idempotent like the BE. Apply is
   *  `config_change`-only and acknowledge `advisory`-only — the BE 409s on the
   *  mismatch rather than performing a no-op. */
  private async transition(
    insightId: string,
    target: "applied" | "acknowledged" | "dismissed",
    verb: string,
  ): Promise<Insight> {
    await this.delay();
    const row = this.insights.get(insightId);
    if (!row) throw notFound(insightId);

    if (target === "applied" && !row.config_suggestion) {
      throw conflict(row.state, "applied — it has no config suggestion");
    }

    if (target === "acknowledged" && row.config_suggestion) {
      throw conflict(row.state, "acknowledged — apply it instead");
    }

    if (row.state === target) return { ...row };

    if (row.state !== "published") throw conflict(row.state, verb);

    const next: Insight = { ...row, state: target, updated_at: NOW };
    this.insights.set(insightId, next);
    return { ...next };
  }
}
